import React from 'react';
import { RobotActivityOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';

interface ComponentProps {
  counters?: RobotActivityOutDTO;
}

const Counter: React.FC<{ label: string; value: number | string; hint?: string; tone?: 'neutral' | 'good' | 'warn' | 'bad' }> = ({ label, value, hint, tone = 'neutral' }) => {
  const toneClass =
    tone === 'good' ? 'text-green-700' : tone === 'warn' ? 'text-amber-700' : tone === 'bad' ? 'text-red-700' : 'text-content-primary';
  return (
    <div className="flex min-w-[9rem] flex-1 flex-col gap-1 rounded-md border border-solid border-base-tertiary bg-white p-3">
      <span className={`text-2xl font-medium ${toneClass}`}>{value}</span>
      <span className="text-sm text-content-secondary">{label}</span>
      {hint && <span className="text-xs text-content-tertiary">{hint}</span>}
    </div>
  );
};

/**
 * Is the robot working, and how hard.
 *
 * Deliberately numbers rather than a chart — "is it moving" is answered faster by a figure that
 * climbs than by a sparkline.
 */
const ActivityCounters: React.FC<ComponentProps> = ({ counters }) => {
  const { tr } = useTranslationLgs();

  if (!counters) return null;

  return (
    <div className="flex flex-wrap gap-3">
      <Counter label={tr('AiDispatcher.inFlight', 'Running now')} value={counters.inFlight} tone={counters.inFlight > 0 ? 'good' : 'neutral'} />
      <Counter
        label={tr('AiDispatcher.dueNow', 'Due now')}
        value={counters.dueNow}
        // Climbing and never falling is what a stuck engine looks like from here.
        hint={tr('AiDispatcher.dueNowHint', 'Queued and ready to run')}
        tone={counters.dueNow > 0 ? 'warn' : 'neutral'}
      />
      <Counter label={tr('AiDispatcher.scheduled', 'Scheduled')} value={counters.scheduled} hint={tr('AiDispatcher.scheduledHint', 'Waiting on a delay')} />
      <Counter label={tr('AiDispatcher.executed24h', 'Acted on (24 h)')} value={counters.executed24h} tone="good" />
      {/* Not a failure: a skipped execution is a rule that looked and decided no. On a busy
          trigger it dwarfs the rest, and reading it as failures is the obvious wrong conclusion. */}
      <Counter
        label={tr('AiDispatcher.skipped24h', 'Considered, no match (24 h)')}
        value={counters.skipped24h}
        hint={tr('AiDispatcher.skipped24hHint', 'Normal — the rule looked and decided no')}
      />
      <Counter label={tr('AiDispatcher.failed24h', 'Failed (24 h)')} value={counters.failed24h} tone={counters.failed24h > 0 ? 'bad' : 'neutral'} />
      <Counter label={tr('AiDispatcher.enabledRules', 'Enabled rules')} value={counters.enabledRules} />
    </div>
  );
};

export default ActivityCounters;
